import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DwhShare } from "../../../api/types/integrationsTypes";
import type { UpdateShareFormFields } from "./updateShareForm";
import {
  formatDwhShareType,
  initialUpdateShareForm,
  isFormEdited,
  mapUpdateShareFormToRequest,
} from "./updateShareForm";

interface Props {
  share: DwhShare;
  onCancel: () => void;
  onShareUpdated: (accountCode: string) => void;
}

const updateDwhShare = withErrorHandling(adminApi.patchDwhShare);

const UpdateShareDialog = ({ share, onCancel, onShareUpdated }: Props) => {
  const [form, setForm] = useState(() => initialUpdateShareForm(share));
  const [errorMessage, setErrorMessage] = useState<string>();
  const [saving, setSaving] = useState(false);

  const handleEdit =
    (field: UpdateShareFormFields): React.ChangeEventHandler<HTMLInputElement> =>
    ({ target }) =>
      setForm((currentForm) => ({
        ...currentForm,
        [field]: target.value,
      }));

  const handleCancel = () => {
    onCancel();
    setSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const [, error] = await updateDwhShare(share.id, mapUpdateShareFormToRequest(form));
    setSaving(false);

    if (error) {
      logError(error, "[UpdateShareDialog] handleSave");
      setErrorMessage(error.message || "Server error");
      return;
    }

    onShareUpdated(share.accountCode.toUpperCase());
  };

  return (
    <SmallDialog open onClose={handleCancel}>
      <DialogTitle>Edit Sharing Description</DialogTitle>
      <DialogCloseButton onClick={handleCancel} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Type
        </Typography>
        <TextField disabled fullWidth variant="outlined" value={formatDwhShareType(share.type)} />
        <Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
          Account Code
        </Typography>
        <TextField disabled fullWidth variant="outlined" value={share.accountCode} />
        <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 1 }}>
          <Typography variant="subtitle2">Description</Typography>
          <Typography color="textSecondary">(optional)</Typography>
        </Stack>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Add text..."
          value={form.description}
          onChange={handleEdit("description")}
          error={!!errorMessage}
          helperText={errorMessage}
          spellCheck
        />
      </DialogContent>
      <DialogButtons onCancel={handleCancel}>
        <Button
          variant="contained"
          color="primary"
          loading={saving}
          onClick={handleSave}
          disabled={!isFormEdited(form, share)}
        >
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default UpdateShareDialog;
